import { ExperienceEventType, IExperience } from '@dadohr/shared/domainTypes/experience';

/**
 * SC-36146 — "your retry is queued, wait a few minutes before retrying again".
 *
 * A successful resend is recorded by the BE as an instance event
 * (`MESSAGE_RESENT` with the message template id, `TASK_RESENT` with the task
 * template id — see new-main functions/schedules/src/resendExperienceItem.ts).
 * Deriving the cooldown from those events makes it survive reloads and apply to
 * every admin looking at the instance. Retries the BE doesn't record (e.g.
 * automation force-runs) fall back to a per-browser mark in localStorage.
 *
 * Pure + import-light so it can be unit-tested without firebase.
 */

export const RETRY_COOLDOWN_MS = 2 * 60 * 1000;

export type RetryTarget =
  | { itemType: 'MESSAGE'; msgTplId: string }
  | { itemType: 'TASK'; taskTplIds: string[] };

type EventLike = { type: string; at?: unknown; msgId?: string; tTId?: string };

/** Shape-agnostic: Timestamp, `{seconds}`/`{_seconds}` map, epoch ms, or missing. */
export const toMillis = (at: unknown): number => {
  if (typeof at === 'number') return at;
  const ts = at as
    | { toMillis?: () => number; seconds?: number; _seconds?: number }
    | null
    | undefined;
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  const seconds = ts.seconds ?? ts._seconds;
  return typeof seconds === 'number' ? seconds * 1000 : 0;
};

/** Newest BE-recorded resend of the target item, or null if never resent. */
export const getLastResendAtMillis = (
  events: IExperience['events'] | undefined,
  target: RetryTarget
): number | null => {
  let last: number | null = null;
  (events as EventLike[] | undefined)?.forEach((e) => {
    const matches =
      target.itemType === 'MESSAGE'
        ? e.type === ExperienceEventType.MESSAGE_RESENT &&
          e.msgId === target.msgTplId
        : e.type === ExperienceEventType.TASK_RESENT &&
          !!e.tTId &&
          target.taskTplIds.includes(e.tTId);
    if (!matches) return;
    const at = toMillis(e.at);
    if (at && (last === null || at > last)) last = at;
  });
  return last;
};

/**
 * Milliseconds left in the cooldown, or 0 when the item may be retried.
 * `failedAtMillis` (when known) is the newest failure for the item: a failure
 * recorded AFTER the retry means the retry's outcome is already known, so the
 * admin may retry straight away.
 */
export const getCooldownRemainingMs = (
  queuedAtMillis: number | null,
  nowMillis: number,
  failedAtMillis: number | null = null
): number => {
  if (queuedAtMillis === null) return 0;
  if (failedAtMillis !== null && failedAtMillis > queuedAtMillis) return 0;
  return Math.max(0, queuedAtMillis + RETRY_COOLDOWN_MS - nowMillis);
};

/** BE resend events across instances within the cooldown window. */
export const countRecentResends = (
  instances: { data: { events?: IExperience['events'] } }[],
  nowMillis: number
): number =>
  instances.reduce(
    (n, d) =>
      n +
      ((d.data.events as EventLike[] | undefined) ?? []).filter(
        (e) =>
          (e.type === ExperienceEventType.MESSAGE_RESENT ||
            e.type === ExperienceEventType.TASK_RESENT) &&
          nowMillis - toMillis(e.at) <= RETRY_COOLDOWN_MS
      ).length,
    0
  );

// ---------------------------------------------------------------------------
// Per-browser fallback for retries without a BE event.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'dado.issueRetryQueuedAt.v1';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

const defaultStorage = (): StorageLike | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

/** Marks younger than the cooldown, keyed by `localRetryKey`. Never throws. */
export const readLocalRetryMarks = (
  nowMillis: number,
  storage: StorageLike | null = defaultStorage()
): Record<string, number> => {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' &&
          nowMillis - entry[1] <= RETRY_COOLDOWN_MS
      )
    );
  } catch {
    return {};
  }
};

/** Records a queued retry; prunes expired marks. Returns the new marks. */
export const recordLocalRetry = (
  key: string,
  nowMillis: number,
  storage: StorageLike | null = defaultStorage()
): Record<string, number> => {
  const next = { ...readLocalRetryMarks(nowMillis, storage), [key]: nowMillis };
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked/full — the in-memory marks still drive this session.
  }
  return next;
};

export const localRetryKey = (orgId: string, instanceId: string, issueKey: string) =>
  `${orgId}:${instanceId}:${issueKey}`;
